import { EventMessage, EventType, InteractionStatus } from "@azure/msal-browser"
import { useIsAuthenticated, useMsal } from "@azure/msal-react"
import MenuIcon from "@mui/icons-material/Menu"
import LinearProgress from "@mui/material/LinearProgress"
import DOMPurify from "isomorphic-dompurify"
import moment from "moment"
import "moment/locale/de"
import { useRouter } from "next/router"
import { FC, ReactElement, useCallback, useEffect, useState } from "react"

// COMMON
import { useBookings } from "../../states/bookings"
import { mergeDialog } from "../../states/dialogs"
import { useCategories } from "../../states/shop/category"
import { useProducts } from "../../states/shop/products"
import { AccountOverview } from "../services/account/overview/AccountOverview"
import { AccountProfile } from "../services/account/profile/AccountProfile"
import { IncidentOverview } from "../services/account/profile/incidents/IncidentOverview"
import { OrderedServices } from "../services/account/service/OrderedServices"
import { Card } from "src/components/common/card"
import { Breadcrumb } from "src/components/core/breadcrumb"
import {
  BasicLayout,
  DialogTypeProps,
  FloatingButtonProps,
  StaticContactProps,
} from "src/components/layouts/basic"

// SERVICES
import { BookingDetail } from "src/components/services/booking/detail"
import { BookingOverview } from "src/components/services/booking/overview"
import { CoExhibitorBookingDetail } from "src/components/services/coExhibitorBooking/detail"
import { CoExhibitorBookingForm } from "src/components/services/coExhibitorBooking/form"
import { MainExhibitorBooking } from "src/components/services/mainExhibitorBooking"
import { ShopCartOverview } from "src/components/services/shop/cart/overview"
import { ShopDetail } from "src/components/services/shop/detail"
import { ShopOrders } from "src/components/services/shop/orders"
import { ShopOrderDetails } from "src/components/services/shop/orders/detail/ShopOrderDetails"
import { ShopOverview } from "src/components/services/shop/overview"
import { ShopStandBookingRequestsOverview } from "src/components/services/shop/stand-booking-requests-overview"

// HELPERS
import { create } from "src/helpers/bem"
import { fetchXRM } from "src/helpers/fetchQueriesXRM"
import { PageTemplates } from "src/helpers/pages"
import { resetStates } from "src/helpers/reset"

// STATES
import { fetchIncidentAuthorization } from "src/queriesXRM/incident"
import { getInvitationAddressBySalesorderId } from "src/queriesXRM/invite"
import { isAccountIncomplete, useAccount } from "src/states/account"
import { useLocale } from "src/states/locale"
import { addNotification } from "src/states/notifications"

import { resetFilter, useFilter } from "src/states/shop/filter"
import { SharedPageProps } from "src/types/SharedPageProps"

import styles from "./ContentPage.module.scss"
import { DefaultPage } from "./DefaultPage"

const bem = create(styles, "ContentPage")

export const ContentPage: FC<SharedPageProps> = (props) => {
  const { messages, page } = props
  const sidebarMessages = messages?.pages?.sidebar
  const [loading, setLoading] = useState(true)
  const [isFetchingXRM, setIsFetchingXRM] = useState(false)
  const [staticContact, setStaticContact] = useState("")
  const redirect = page.data?.redirect
  const template = page.data?.template?.templateName as
    | PageTemplates
    | null
    | undefined
  const router = useRouter()
  const locale = useLocale()
  const account = useAccount()
  const { filterBy } = useFilter()
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const { loading: loadingBookings } = useBookings()
  const { loading: loadingProducts } = useProducts()
  const hasImcompleteAccount = isAccountIncomplete()
  const { loading: loadingCategories } = useCategories()
  const redirectRegistration =
    router?.asPath === `/de/registrierung/` ||
    router?.asPath === `/en/registration/`

  const isLoadingShop =
    (isAuthenticated && loadingBookings) || loadingProducts || loadingCategories

  const validateAuthentication = useCallback(async () => {
    setLoading(true)
    if (redirectRegistration && isAuthenticated) {
      await router.push("/")
      setLoading(false)
      return
    }

    // in rare cases is the account not filled. Try to fetch data before redirecting
    if (
      redirect?.profileCompleted &&
      isAccountIncomplete() &&
      isAuthenticated
    ) {
      setIsFetchingXRM(true)
      await fetchXRM(locale)
      setIsFetchingXRM(false)
    }

    if (redirect?.loggedIn && isAccountIncomplete() && !isAuthenticated) {
      const redirectURL =
        locale === "de" ? "/de/registrierung" : "/en/registration"
      await router.push(redirectURL)
      setLoading(false)
      return addNotification({
        variant: "warning",
        code: "not/loggedIn",
        autoclose: true,
      })
    }
    if (redirect?.profileCompleted && isAccountIncomplete()) {
      const accountURL = locale === "de" ? "/de/konto" : "/en/account"
      await router.push(accountURL)
      setLoading(false)
      return addNotification({
        variant: "warning",
        code: "account/info/missing",
        autoclose: true,
      })
    }
    setLoading(false)
  }, [
    isAuthenticated,
    locale,
    redirect?.loggedIn,
    redirect?.profileCompleted,
    redirectRegistration,
    router,
  ])

  const redirectToProfileComplete = useCallback(async () => {
    const accountURL = locale === "de" ? "/de/konto" : "/en/account"
    await router.push(accountURL)
    mergeDialog("completeProfileDialog", { open: true })
    return addNotification({
      variant: "warning",
      code: "account/info/missing",
      autoclose: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const getMainExhibitorAccount = async () => {
    const url = location.search
    const params = new URLSearchParams(url)
    const salesorderId = params.get("invitationcode") as string
    if (salesorderId) {
      const account = await getInvitationAddressBySalesorderId(salesorderId)
      if (account) {
        setStaticContact(`
          <p>
            ${account?.clientAddressAccount1}<br>
            ${account?.clientAddressStreet1}<br>
            ${account?.clientAddressZipCode} ${account?.clientAddressCity}<br>
            ${account?.clientAddressCountry}<br>
          </p>
        `)
      }
    }
  }

  useEffect(() => {
    if (
      isAuthenticated &&
      (template === "Co-Exhibitor Booking Form Template" ||
        template === "Co-Exhibitor Booking View Template")
    ) {
      getMainExhibitorAccount()
    }
  }, [isAuthenticated, locale, router.asPath, template])

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      validateAuthentication()
    }
  }, [isAuthenticated, router.asPath, inProgress, validateAuthentication])

  useEffect(() => {
    if (isAuthenticated) {
      fetchIncidentAuthorization()
    }
  }, [isAuthenticated])

  useEffect(() => {
    moment.locale(locale)
    if (isAuthenticated) {
      fetchXRM(locale)
    }
  }, [isAuthenticated, locale])

  useEffect(() => {
    const callbackId = instance.addEventCallback(
      async (message: EventMessage) => {
        if (message.eventType === EventType.LOGOUT_START) {
          resetStates()
        }
        if (
          message.eventType === EventType.LOGIN_SUCCESS ||
          message.eventType === EventType.ACCOUNT_ADDED
        ) {
          setLoading(true)
          setIsFetchingXRM(true)
          await fetchXRM(locale)
          setIsFetchingXRM(false)

          if (isAccountIncomplete()) {
            await redirectToProfileComplete()
          }

          resetFilter()
          setLoading(false)
        }
      },
    )

    return () => {
      // Cleanup on component unmount
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
  }, [instance, locale, redirectToProfileComplete])

  useEffect(() => {
    const navigateToProfileComplete = async () => {
      setLoading(true)
      await redirectToProfileComplete()
      setLoading(false)
    }

    if (
      hasImcompleteAccount &&
      isAuthenticated &&
      !isFetchingXRM &&
      inProgress !== InteractionStatus.Logout
    ) {
      navigateToProfileComplete()
    }
  }, [
    isAuthenticated,
    isFetchingXRM,
    hasImcompleteAccount,
    inProgress,
    redirectToProfileComplete,
  ])

  if (loading) {
    return <LinearProgress color="primary" />
  }

  const renderLayout = (
    children: ReactElement,
    noHeadline?: boolean,
    staticContact?: StaticContactProps,
    loadDialogs?: DialogTypeProps[],
    bgColor?: "primary" | "secondary" | "white",
    floatingButton?: FloatingButtonProps,
  ) => (
    <BasicLayout
      {...props}
      bgColor={bgColor}
      floatingButton={floatingButton}
      loadDialogs={loadDialogs}
      staticContact={staticContact}
    >
      <Breadcrumb items={page?.data?.meta?.breadcrumb} />
      {page?.data?.sidebar?.info?.content ? (
        <Card>
          <div className={bem("card__content")}>
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(page.data.sidebar.info.content),
              }}
            />
          </div>
        </Card>
      ) : null}
      {noHeadline ? null : <h1>{page?.data?.title}</h1>}
      {children}
    </BasicLayout>
  )

  const sanitizedContent = DOMPurify.sanitize(props?.page?.data?.content ?? "")
  switch (template) {
    case "Account Overview Template":
      return renderLayout(
        <AccountOverview {...props}>
          <AccountProfile {...props} account={account} />
        </AccountOverview>,
        true,
        undefined,
        ["EditProfile", "AddEditContact", "ManageContacts", "CompleteProfile"],
      )

    case "Booking Overview Template":
      return renderLayout(
        <AccountOverview {...props}>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
          <BookingOverview {...props} />
        </AccountOverview>,
        true,
      )
    case "Booking Detail Template":
      return renderLayout(
        <AccountOverview {...props}>
          <BookingDetail {...props} />
        </AccountOverview>,
        true,
        undefined,
        [
          "AddCoExhibitor",
          "EditCoExhibitor",
          "DeleteCoExhibitor",
          "Permission",
          "DeleteFile",
          "RejectPlacement",
        ],
      )
    case "Webshop Incidents Template":
      return renderLayout(
        <AccountOverview {...props}>
          <OrderedServices {...props} />
        </AccountOverview>,
        true,
        undefined,
        ["Permission"],
      )
    case "Account Incidents Template":
      return renderLayout(
        <AccountOverview {...props}>
          <IncidentOverview {...props} />
        </AccountOverview>,
        true,
        undefined,
        ["EditProfile", "AddEditContact", "Incident", "CompleteProfile"],
      )
    case "Booking Form Template":
      return renderLayout(
        <MainExhibitorBooking {...props} />,
        undefined,
        undefined,
        undefined,
        "white",
      )
    case "Co-Exhibitor Booking Form Template":
      return renderLayout(
        <CoExhibitorBookingForm {...props} />,
        undefined,
        staticContact
          ? {
              title: sidebarMessages?.staticContact?.title,
              content: staticContact,
            }
          : undefined,
        undefined,
        "white",
      )
    case "Co-Exhibitor Booking View Template":
      return renderLayout(
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
          <CoExhibitorBookingDetail {...props} />
        </>,
        undefined,
        staticContact
          ? {
              title: sidebarMessages?.staticContact?.title,
              content: staticContact,
            }
          : undefined,
      )
    case "Webshop Overview Template":
      return renderLayout(
        <ShopOverview {...props} />,
        true,
        undefined,
        [
          "Permission",
          "ShopOverviewStandRequest",
          "ShopDetailProductFeature",
          "ShopCart",
          "ManageContacts",
          "ShopOverviewContact",
        ],
        "primary",
        !isLoadingShop && filterBy?.category
          ? {
              sx: (theme) => ({
                position: "fixed",
                top: "50%",
                right: 0,
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.main,
                },
              }),
              icon: (
                <MenuIcon
                  sx={{ width: "37px", height: "35px", color: "#FFFFFF" }}
                />
              ),
              onClick: () =>
                mergeDialog("shopOverviewContactDialog", { open: true }),
            }
          : undefined,
      )
    case "Webshop Product Detail Template":
      return renderLayout(
        <ShopDetail {...props} />,
        true,
        undefined,
        ["ManageContacts", "ShopDetailProductFeature", "ShopCart"],
        "primary",
      )
    case "Webshop Standrequests Overview Template":
      return renderLayout(
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
          <ShopStandBookingRequestsOverview {...props} />
        </>,
        false,
        undefined,
        undefined,
        "primary",
      )
    case "Webshop Orders Template":
      return renderLayout(
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
          <ShopOrders {...props} />
        </>,
        true,
        undefined,
        undefined,
        "primary",
      )
    case "Webshop Order Detail Template":
      return renderLayout(
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
          <ShopOrderDetails {...props} />
        </>,
        true,
        undefined,
        ["ShopOrderDetailsComplain", "ShopOrderDetailsCancelOrder"],
        "primary",
      )
    case "Webshop Cart Overview Template":
      return renderLayout(
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
          <ShopCartOverview {...props} />
        </>,
        false,
        undefined,
        [
          "ManageContacts",
          "ShopDetailProductFeature",
          "ShopOrderOverviewRedirect",
        ],
        "primary",
      )
    default:
      return renderLayout(
        <DefaultPage {...props}>
          <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
        </DefaultPage>,
        true,
        undefined,
        undefined,
      )
  }
}
